"use client";

import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import { themeScopeForPath } from "@/lib/theme";
import {
  resolvedThemeFor,
  systemPrefersDark,
  useThemeStore,
} from "@/stores/theme-store";

/**
 * `useLayoutEffect` on the client, `useEffect` on the server — the standard
 * isomorphic shim, and it is load-bearing here (see below). Selected once at
 * module scope so the hook call itself stays unconditional; `useLayoutEffect`
 * would otherwise warn during SSR, where it never runs.
 */
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Applies the route's theme to `<html data-theme>`, and re-applies it on
 * client-side (SPA) navigation.
 *
 * The pre-paint `THEME_INIT_SCRIPT` only runs on a FULL page load, so a
 * client-side navigation ACROSS route groups (a staff member clicking the
 * storefront's "Admin dashboard" pill, or vice-versa) would otherwise keep the
 * previous group's attribute:
 *
 *  - a customer-facing path → force LIGHT. Customers have no theme toggle and
 *    the storefront is light-only, so a staff Dark/Auto preference must not
 *    follow them onto customer pages. Does NOT touch the stored preference, so
 *    it survives for when they return to a portal.
 *  - `/admin` or `/florist` → apply the stored `light`/`dark`/`system`
 *    preference (the same value the toggle and the ⌘K palette write).
 *
 * TWO THINGS THIS DELIBERATELY DOES NOT DO, both of which it used to:
 *
 *  1. It does not take a `variant` prop. The scope is a pure function of the
 *     URL — `themeScopeForPath(usePathname())` — and `lib/theme.ts` shares that
 *     one predicate with the pre-paint script, so the client can no longer
 *     disagree with the inline script about what counts as a staff route.
 *  2. It does not publish the scope into the theme store from a mount effect.
 *     Storing it made the value a stale mirror for one render after a
 *     cross-group navigation, and because the write happened in a PASSIVE
 *     effect that frame could be PAINTED at the wrong theme. The resolved theme
 *     is now computed during render and written in a LAYOUT effect, which runs
 *     inside the commit — before the browser paints.
 *
 * Renders nothing.
 */
export function ThemeScope() {
  const pathname = usePathname();
  const pref = useThemeStore((s) => s.pref);
  const systemDark = useThemeStore((s) => s.systemDark);
  const setSystemDark = useThemeStore((s) => s.setSystemDark);

  // Derived, never stored — the whole point of this component.
  const resolved = resolvedThemeFor({
    pref,
    systemDark,
    scope: themeScopeForPath(pathname),
  });

  // Refresh the OS setting so an entering "system" preference resolves against
  // the current value. Passive is fine: the store outlives every navigation, so
  // by the time a customer reaches a portal this has long since run.
  useEffect(() => {
    setSystemDark(systemPrefersDark());
  }, [pathname, setSystemDark]);

  useIsomorphicLayoutEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  return null;
}
